import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Bug, 
  ArrowLeft, 
  ExternalLink, 
  Mail, 
  Cpu,
  Zap,
  Target,
  Lightbulb,
  Code
} from "lucide-react";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      {/* Navigation */}
      <nav className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-sm bg-foreground flex items-center justify-center">
              <Bug className="w-5 h-5 text-background" />
            </div>
            <span className="font-black text-xl tracking-tight font-heading">RAG Debugger</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="secondary" className="mb-6">
            About the Builder
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight font-heading mb-6">
            Built by a developer,<br />for developers
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            The story behind RAG Debugger and the journey of making AI debugging accessible.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-muted/30 border border-border rounded-sm p-8 lg:p-12">
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              {/* Avatar */}
              <div className="flex-shrink-0 mx-auto lg:mx-0">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                  VN
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold font-heading">Vasanth Natarajan</h2>
                  <p className="text-muted-foreground">AI Engineer & Builder</p>
                </div>
                
                <div className="prose prose-slate max-w-none space-y-5 text-muted-foreground leading-relaxed">
                  <p>
                    Hi, I'm Vasanth Natarajan. I built RAG Debugger to make working with AI and 
                    retrieval-augmented systems easier, more transparent, and more interactive. 
                    This SDK is part of a larger exploration into how generative AI and agentic AI 
                    can be integrated into real-world applications—from contact centers to intelligent 
                    assistants.
                  </p>
                  <p>
                    The idea came from my own experience debugging complex RAG pipelines. Piecing 
                    together retrievers, prompts, and LLMs can get messy fast. I wanted a tool that 
                    shows what's happening under the hood, step by step, so developers can iterate 
                    faster, experiment safely, and learn from each run.
                  </p>
                  <p>
                    RAG Debugger is designed for easy adoption: drop it into your project, simulate 
                    queries with fake LLMs or retrievers, and instantly see traces, token usage, and 
                    response paths—all in one dashboard. It's built to help you prototype, understand, 
                    and optimize your AI workflows without getting lost in the complexity.
                  </p>
                  <p>
                    This project is part of a bigger journey—my ongoing experiments, newsletters, and 
                    AI labs exploring cutting-edge AI architectures, memory management, and agentic 
                    systems. If you want to stay updated on what I'm building next, follow my AI Labs 
                    and subscribe to my newsletters. Together, we can push the boundaries of what AI 
                    can do in practical, impactful ways.
                  </p>
                </div>

                {/* Links */}
                <div className="flex flex-wrap gap-3 mt-8">
                  <a 
                    href="https://vasanthnatarajan.me/ai-labs" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-foreground text-background rounded-sm text-sm font-medium hover:bg-foreground/90 transition-colors"
                  >
                    <Cpu className="w-4 h-4" />
                    AI Labs
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a 
                    href="https://vasanthnatarajan.me/newsletter" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-border rounded-sm text-sm font-medium hover:bg-muted transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Newsletter
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a 
                    href="https://vasanthnatarajan.me" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-border rounded-sm text-sm font-medium hover:bg-muted transition-colors"
                  >
                    Website
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What I'm Building */}
      <section className="py-16 lg:py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold font-heading text-center mb-12">What I'm Exploring</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-background border border-border rounded-sm p-6">
              <Zap className="w-8 h-8 mb-4 text-yellow-500" />
              <h3 className="font-bold mb-2">Agentic AI Systems</h3>
              <p className="text-sm text-muted-foreground">
                Building autonomous AI agents that can reason, plan, and execute complex tasks 
                with minimal human intervention.
              </p>
            </div>
            <div className="bg-background border border-border rounded-sm p-6">
              <Target className="w-8 h-8 mb-4 text-red-500" />
              <h3 className="font-bold mb-2">RAG Optimization</h3>
              <p className="text-sm text-muted-foreground">
                Exploring advanced retrieval strategies, hybrid search, and context optimization 
                for better AI responses.
              </p>
            </div>
            <div className="bg-background border border-border rounded-sm p-6">
              <Lightbulb className="w-8 h-8 mb-4 text-blue-500" />
              <h3 className="font-bold mb-2">Memory Management</h3>
              <p className="text-sm text-muted-foreground">
                Developing techniques for AI systems to maintain context and learn from 
                interactions over time.
              </p>
            </div>
            <div className="bg-background border border-border rounded-sm p-6">
              <Code className="w-8 h-8 mb-4 text-green-500" />
              <h3 className="font-bold mb-2">Developer Tools</h3>
              <p className="text-sm text-muted-foreground">
                Creating tools that make AI development more accessible, debuggable, and 
                production-ready.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold font-heading mb-4">Ready to debug your RAG pipeline?</h2>
          <p className="text-muted-foreground mb-8">
            Get started for free and see what's happening inside your AI.
          </p>
          <Link to="/auth">
            <Button size="lg" className="h-12 px-8">
              Get Started Free
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-sm bg-foreground flex items-center justify-center">
                <Bug className="w-4 h-4 text-background" />
              </div>
              <span className="font-bold">RAG Debugger</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Built by <a href="https://vasanthnatarajan.me" target="_blank" rel="noopener noreferrer" className="hover:text-foreground underline">Vasanth Natarajan</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
